import * as designTokens from '@calm/evexia-design-tokens';
import { CSSProperties } from 'react';

// Shared font stack: the brand family primitive (Figtree) followed by the
// platform fallback chain. The composed string is byte-identical to the legacy
// full-stack token while the brand family now reads through PRIMITIVES.
export const FONT_FAMILY = `"${designTokens.PRIMITIVES.typography.family.primary}", -apple-system, Arial, BlinkMacSystemFont, Helvetica Neue, Helvetica, sans-serif`;

const typographyVariants: Record<string, CSSProperties> = {
    h1: {
        fontSize: designTokens.DS_FONT_SIZE.H1,
        fontWeight: designTokens.PRIMITIVES.typography.weight._400,
        fontFamily: FONT_FAMILY,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L_2,
    },
    h2: {
        fontSize: designTokens.DS_FONT_SIZE.H2,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._400,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L_1,
    },
    h3: {
        fontSize: designTokens.DS_FONT_SIZE.H3,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._400,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L0,
    },
    h4: {
        fontSize: designTokens.DS_FONT_SIZE.H4,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._500,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L4,
    },
    h5: {
        fontSize: designTokens.PRIMITIVES.typography.size._24,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._500,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L0,
    },
    h6: {
        fontSize: designTokens.PRIMITIVES.typography.size._20,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._500,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L3,
    },
    subtitle1: {
        fontSize: designTokens.DS_FONT_SIZE.NORMAL_LARGE,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._400,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L3,
        lineHeight: 1.5,
    },
    subtitle2: {
        fontSize: designTokens.PRIMITIVES.typography.size._14,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._500,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L1,
    },
    body1: {
        fontSize: designTokens.PRIMITIVES.typography.size._16,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._400,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L3,
    },
    body2: {
        fontSize: designTokens.PRIMITIVES.typography.size._14,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._600,
        fontStyle: 'normal',
        textTransform: 'none',
        // Figma specifies 0.17px here (and 0.16px for Components/Chip); both are
        // intentionally held at L3 = 0.15px for now — sub-pixel, and the
        // DS_LETTER_SPACING ladder has no slot for these values without
        // renumbering the native exporters. Revisit if the letter-spacing scale
        // is reworked (see EVE-12388 / the Chip ticket).
        letterSpacing: designTokens.DS_LETTER_SPACING.L3,
    },
    // Same as Button Medium
    button: {
        fontSize: designTokens.PRIMITIVES.typography.size._14,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._600,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L5,
    },
    caption: {
        fontSize: designTokens.PRIMITIVES.typography.size._12,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._600,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L5,
    },
    overline: {
        fontSize: designTokens.PRIMITIVES.typography.size._12,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._400,
        fontStyle: 'normal',
        textTransform: 'uppercase',
        letterSpacing: designTokens.DS_LETTER_SPACING.L7,
    },

    /* Non-standard variants */
    body1Demi: {
        fontSize: designTokens.PRIMITIVES.typography.size._16,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._600,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L3,
    },
    buttonLarge: {
        fontSize: designTokens.DS_FONT_SIZE.BUTTON_LARGE,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._600,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L6,
    },
    buttonSmall: {
        fontSize: designTokens.PRIMITIVES.typography.size._13,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._600,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L6,
    },
    avatarLetter: {
        fontSize: designTokens.PRIMITIVES.typography.size._20,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._400,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L2,
    },
    inputLabel: {
        fontSize: designTokens.PRIMITIVES.typography.size._12,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._400,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L3,
    },
    inputText: {
        fontSize: designTokens.PRIMITIVES.typography.size._16,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._400,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L3,
    },
    helperText: {
        fontSize: designTokens.PRIMITIVES.typography.size._12,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._400,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L5,
    },
    chip: {
        fontSize: designTokens.PRIMITIVES.typography.size._13,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._400,
        fontStyle: 'normal',
        textTransform: 'none',
        lineHeight: `${designTokens.PRIMITIVES.typography.lineHeight._18}px`,
        letterSpacing: designTokens.DS_LETTER_SPACING.L3,
    },
    tooltip: {
        fontSize: designTokens.DS_FONT_SIZE.TOOLTIP,
        fontFamily: FONT_FAMILY,
        fontWeight: designTokens.PRIMITIVES.typography.weight._500,
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: designTokens.DS_LETTER_SPACING.L0,
    },
    srOnly: {
        position: 'absolute',
        height: '1px',
        width: '1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        clipPath: 'inset(50%)',
        whiteSpace: 'nowrap',
    },
};

export default typographyVariants;
